import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import { AdditiveBlending, SRGBColorSpace } from 'three';
import { softSpriteTexture } from './softSprite.js';

// Whole solar system for the contact beat: emissive mini-star + textured
// planets on faint orbit lines, angular speeds ∝ a^-1.5 (Kepler) — slow,
// correct, background-object energy.
const PLANETS = [
  { a: 0.85, r: 0.045, map: '/assets/textures/moon_2k.jpg' },
  { a: 1.35, r: 0.075, map: '/assets/textures/jupiter_2k.jpg' },
  { a: 1.95, r: 0.065, map: '/assets/textures/neptune_2k.jpg', ring: true },
  { a: 2.55, r: 0.05, map: '/assets/textures/moon_2k.jpg' },
];
const BASE_SPEED = 0.22;

export function SolarSystem({ position = [0, -0.2, -2.4], scale = 1 }) {
  const groupRef = useRef();
  const maps = useTexture(
    PLANETS.map((p) => p.map),
    (textures) => {
      (Array.isArray(textures) ? textures : [textures]).forEach((t) => {
        t.colorSpace = SRGBColorSpace;
      });
    },
  );
  const glow = softSpriteTexture({
    stops: [
      [0, 'rgba(255,214,150,0.9)'],
      [0.4, 'rgba(255,178,92,0.25)'],
      [1, 'rgba(255,140,60,0)'],
    ],
  });

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    groupRef.current.children.forEach((child) => {
      if (child.userData.a) {
        const omega = BASE_SPEED / Math.pow(child.userData.a, 1.5);
        const angle = child.userData.phase + t * omega;
        child.position.set(Math.cos(angle) * child.userData.a, 0, Math.sin(angle) * child.userData.a);
      }
    });
  });

  return (
    <group position={position} scale={scale} rotation={[0.42, 0, 0.06]}>
      <group ref={groupRef}>
        {/* star */}
        <mesh>
          <sphereGeometry args={[0.16, 32, 16]} />
          <meshBasicMaterial color={[2.6, 1.8, 1]} toneMapped={false} />
        </mesh>
        <sprite scale={1.3}>
          <spriteMaterial map={glow} color="#ffc46b" transparent depthWrite={false} blending={AdditiveBlending} opacity={0.9} />
        </sprite>
        {PLANETS.map((planet, index) => (
          <group key={planet.a} userData={{ a: planet.a, phase: index * 1.7 }}>
            <mesh>
              <sphereGeometry args={[planet.r, 24, 12]} />
              <meshStandardMaterial map={maps[index]} roughness={1} />
            </mesh>
            {planet.ring && (
              <mesh rotation={[Math.PI / 2.3, 0, 0]}>
                <ringGeometry args={[planet.r * 1.5, planet.r * 2.3, 48]} />
                <meshBasicMaterial color="#9fb2cf" transparent opacity={0.35} side={2} depthWrite={false} />
              </mesh>
            )}
          </group>
        ))}
      </group>
      {/* orbit lines */}
      {PLANETS.map((planet) => (
        <mesh key={`orbit-${planet.a}`} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[planet.a, 0.0035, 6, 128]} />
          <meshBasicMaterial color="#8b95a8" transparent opacity={0.22} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}
